import React from 'react';
import { Link } from 'react-router-dom';
import { Home as HomeIcon, Camera, CheckCircle, Clock, ArrowRight, CalendarCheck, FileImage, AlertTriangle, Shield, Smartphone, ClipboardList } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

export default function InspectionsPage() {
  const inspectionTypes = [
    {
      name: "Inventory & Check-In",
      description: "Comprehensive documentation of property condition at tenancy start. 200+ photos, detailed room-by-room reports, meter readings.",
      icon: Camera,
      features: ["Photo evidence", "Condition grades", "Meter readings", "Key handover"]
    },
    {
      name: "Mid-Tenancy Inspection",
      description: "Periodic checks to ensure property is being maintained. Identify issues early, protect your investment.",
      icon: ClipboardList,
      features: ["Compliance check", "Maintenance issues", "Tenant welfare", "Photo comparison"]
    },
    {
      name: "Check-Out Inspection",
      description: "End of tenancy assessment with comparison to check-in. Clear evidence for deposit discussions.",
      icon: FileImage,
      features: ["Before/after comparison", "Damage assessment", "Cleaning check", "Deposit recommendations"]
    }
  ];

  const process = [
    {
      step: "1",
      title: "Schedule",
      description: "Book via the platform. We coordinate with tenants automatically."
    },
    {
      step: "2",
      title: "Inspect",
      description: "Our vetted inspectors attend with professional equipment."
    },
    {
      step: "3",
      title: "Report",
      description: "Detailed report with photos uploaded within 24 hours."
    },
    {
      step: "4",
      title: "Action",
      description: "Issues flagged, maintenance requests raised automatically."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="p-2 bg-primary rounded-lg">
              <HomeIcon className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-display font-extrabold text-primary">LetWise</h1>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/for-landlords" className="text-slate-700 hover:text-primary transition-colors font-semibold">
              For Landlords
            </Link>
            <Link to="/for-tenants" className="text-slate-700 hover:text-primary transition-colors font-semibold">
              For Tenants
            </Link>
            <Link to="/properties" className="text-slate-600 hover:text-primary transition-colors font-medium">
              Browse Properties
            </Link>
            <Link to="/login"><Button>Sign In</Button></Link>
          </nav>
          <div className="flex md:hidden items-center gap-3">
            <Link to="/for-landlords" className="text-sm text-slate-700 font-medium">Landlords</Link>
            <Link to="/for-tenants" className="text-sm text-slate-700 font-medium">Tenants</Link>
            <Link to="/login"><Button size="sm">Sign In</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-sm font-medium mb-6">
            <Camera className="w-4 h-4" />
            Professional Property Inspections
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
            Property Inspections Made Simple
          </h1>
          <p className="text-xl opacity-90 max-w-3xl mx-auto">
            Professional inspections with detailed reports, photo evidence, and automatic maintenance flagging. 
            Protect your property and maintain great tenant relationships.
          </p>
        </div>
      </section>

      {/* Inspection Types */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-display font-bold text-center mb-12">Inspection Services</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {inspectionTypes.map((type, idx) => (
              <Card key={idx} className="p-6 hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                  <type.icon className="w-7 h-7 text-purple-600" />
                </div>
                <h3 className="text-xl font-display font-bold mb-3">{type.name}</h3>
                <p className="text-slate-600 mb-4">{type.description}</p>
                <ul className="space-y-2">
                  {type.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {f}
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-display font-bold text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {process.map((p, idx) => (
              <div key={idx} className="text-center">
                <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {p.step}
                </div>
                <h3 className="font-bold text-lg mb-2">{p.title}</h3>
                <p className="text-slate-600 text-sm">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-display font-bold mb-6">Digital-First Inspections</h2>
              <p className="text-lg text-slate-600 mb-6">
                Our inspections are designed for the modern landlord. Everything is digital, 
                timestamped, and stored securely in the cloud.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Smartphone className="w-6 h-6 text-primary mt-1" />
                  <div>
                    <h4 className="font-semibold">Mobile App</h4>
                    <p className="text-slate-600 text-sm">Tenants can self-report issues with photos</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="w-6 h-6 text-primary mt-1" />
                  <div>
                    <h4 className="font-semibold">24hr Reports</h4>
                    <p className="text-slate-600 text-sm">Full inspection report within 24 hours</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-primary mt-1" />
                  <div>
                    <h4 className="font-semibold">Auto-Flagging</h4>
                    <p className="text-slate-600 text-sm">Issues automatically create maintenance requests</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Shield className="w-6 h-6 text-primary mt-1" />
                  <div>
                    <h4 className="font-semibold">Dispute Evidence</h4>
                    <p className="text-slate-600 text-sm">Photo evidence for deposit discussions</p>
                  </div>
                </li>
              </ul>
            </div>
            <Card className="p-8 bg-gradient-to-br from-purple-50 to-indigo-50">
              <h3 className="text-2xl font-display font-bold mb-6 text-center">Inspection Pricing</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-white rounded-lg">
                  <span className="font-medium">Check-In/Out Inventory</span>
                  <span className="text-xl font-bold text-primary">£95</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-white rounded-lg">
                  <span className="font-medium">Mid-Tenancy Inspection</span>
                  <span className="text-xl font-bold text-primary">£65</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-white rounded-lg">
                  <span className="font-medium">HMO Room Inspection</span>
                  <span className="text-xl font-bold text-primary">£35</span>
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-4 text-center">
                All prices include report, photos, and digital storage
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* AI Smart Inspection Engine */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-sm font-medium mb-6">
                <Camera className="w-4 h-4" />
                AI Smart Inspection Engine
              </div>
              <h2 className="text-4xl font-display font-bold mb-6">
                Intelligent Damage Detection
              </h2>
              <p className="text-xl opacity-90 mb-8">
                Our AI-powered inspection engine automatically compares baseline and current property conditions, 
                detecting changes, wear patterns, and potential issues that might be missed by human inspection alone.
              </p>
              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-300" />
                  <span>Automated before/after comparison</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-300" />
                  <span>Damage severity classification</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-300" />
                  <span>Fair wear vs. damage distinction</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-300" />
                  <span>Evidence-based deposit recommendations</span>
                </li>
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center">
              <div className="w-24 h-24 mx-auto mb-6 bg-white/20 rounded-full flex items-center justify-center">
                <Camera className="w-12 h-12" />
              </div>
              <h3 className="text-2xl font-bold mb-4">How It Works</h3>
              <div className="text-left space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">1</div>
                  <span>Baseline scan captures property condition at move-in</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">2</div>
                  <span>Tenant submits inspection photos during tenancy</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">3</div>
                  <span>AI engine compares and flags changes automatically</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">4</div>
                  <span>Reports generated with clear evidence for disputes</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-display font-bold mb-4">Protect Your Investment</h2>
          <p className="text-xl opacity-90 mb-8">Professional inspections give you peace of mind.</p>
          <Link to="/register?role=landlord">
            <Button size="lg" variant="secondary" className="text-primary">
              Start Managing <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-2 bg-primary rounded-lg">
              <HomeIcon className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-display font-bold">LetWise</span>
          </div>
          <div className="border-t border-slate-800 pt-8 text-sm text-slate-500">
            © 2025 LetWise. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
